/**
 * FmGrowthChart — line, area and stacked-area trajectories.
 *
 * Series colour is a descriptor: species series by code, other series by tone
 * or the neutral sequence, so a growth line never borrows a species slot.
 * Missing values stay gaps (`connectNulls={false}`).
 */
import React from 'react';
import { Area, AreaChart, CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import type { FmChartRow, FmChartSize, FmGrowthChartSpec } from './chartTypes';
import {
  CHART_AXIS_FONT_SIZE,
  CHART_GRID_PROPS,
  chartMargin,
  CHART_XAXIS_PROPS,
  CHART_YAXIS_PROPS,
  xAxisTitle,
  yAxisTitle,
} from './FmChartChrome.constants';
import { FmChartTooltip } from './FmChartChrome';
import { FmChartFrame } from './FmChartFrame';
import { axisTickFormatter, evenTicks } from './chartAxes';
import { resolveSeriesColor } from './seriesColor';

export type FmGrowthChartProps = Omit<FmGrowthChartSpec, 'kind'> & {
  size?: FmChartSize;
  'data-testid'?: string;
};

const ON_DARK_AXIS = {
  tick: { fill: 'var(--fm-fg-on-dark-muted)', fontSize: CHART_AXIS_FONT_SIZE },
  axisLine: { stroke: 'var(--fm-glass-border)' },
  tickLine: { stroke: 'var(--fm-glass-border)' },
};

/**
 * A numeric x (years) is spaced by value. The domain ends at the first and
 * last served x, and the ticks sit on one round step so years read evenly
 * spaced. Two rows on one x (the state before and after a harvest) draw a
 * vertical drop at that year.
 */
function numericXAxis(data: readonly FmChartRow[], xKey: string) {
  const values = data.flatMap((row) => {
    const value = row[xKey];
    return typeof value === 'number' && Number.isFinite(value) ? [value] : [];
  });
  if (values.length === 0 || values.length !== data.length) return undefined;
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) {
    return {
      type: 'number' as const,
      ticks: [min],
      domain: [min - 1, max + 1] as [number, number],
    };
  }
  return {
    type: 'number' as const,
    ticks: evenTicks(min, max, MAX_X_TICKS),
    domain: [min, max] as [number, number],
  };
}

const MAX_X_TICKS = 7;

export const FmGrowthChart: React.FC<FmGrowthChartProps> = ({
  data,
  xKey,
  xLabel,
  yLabel,
  series,
  render = 'line',
  surface = 'default',
  areaEmphasis = 'standard',
  showDataPoints = false,
  valueDomain = 'zero-baseline',
  grid = 'horizontal',
  formatValue,
  size = 'md',
  'data-testid': testId,
}) => {
  const rows = data.map((row) => ({ ...row }));
  const isArea = render !== 'line';
  const ChartComponent = isArea ? AreaChart : LineChart;
  const onDark = surface === 'on-dark';
  const axisOverride = onDark ? ON_DARK_AXIS : undefined;
  const gridProps = onDark
    ? { ...CHART_GRID_PROPS, stroke: 'var(--fm-glass-border)' }
    : CHART_GRID_PROPS;
  const numericX = numericXAxis(data, xKey);
  return (
    <FmChartFrame size={size} data-testid={testId} data-kind="growth">
      <ChartComponent data={rows} margin={chartMargin(yLabel)} accessibilityLayer>
        {grid === 'horizontal' ? <CartesianGrid {...gridProps} /> : null}
        <XAxis
          dataKey={xKey}
          {...CHART_XAXIS_PROPS}
          {...axisOverride}
          {...numericX}
          label={xAxisTitle(xLabel)}
        />
        <YAxis
          {...CHART_YAXIS_PROPS}
          {...axisOverride}
          domain={valueDomain === 'data-extent' ? ['auto', 'auto'] : undefined}
          tickFormatter={axisTickFormatter}
          label={yAxisTitle(yLabel)}
        />
        <Tooltip content={<FmChartTooltip formatValue={formatValue} />} />
        {series.map((entry) => {
          const color = resolveSeriesColor(entry.color);
          const dot = showDataPoints ? { r: 3, fill: color, strokeWidth: 0 } : false;
          if (isArea) {
            return (
              <Area
                key={entry.key}
                dataKey={entry.key}
                name={entry.label}
                type="linear"
                stroke={color}
                fill={color}
                strokeWidth={2}
                fillOpacity={areaEmphasis === 'subtle' ? 0.1 : 0.3}
                stackId={render === 'stacked-area' ? 'stack' : undefined}
                connectNulls={false}
                dot={dot}
                isAnimationActive={false}
              />
            );
          }
          return (
            <Line
              key={entry.key}
              dataKey={entry.key}
              name={entry.label}
              type="linear"
              stroke={color}
              strokeWidth={2}
              connectNulls={false}
              dot={dot}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          );
        })}
      </ChartComponent>
    </FmChartFrame>
  );
};
